import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Grid, 
  CircularProgress, 
  Alert, 
  Snackbar 
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import axios from "../utils/axiosConfig";

//import { handleApiError } from '../utils/apiErrorHandler';

interface EmailEditorProps {
  registrationConfirmationId: string;
}

interface EmailTemplateData {
  recipientEmail: string;
  ccEmail: string;
  subject: string;
  templateText: string;
  competitionName: string;
  clubName: string;
  contactFirstName: string;
  contactLastName: string;
  totalAmount: number;
}

const EmailEditorPage: React.FC<EmailEditorProps> = ({ registrationConfirmationId }) => {
  const [emailData, setEmailData] = useState<EmailTemplateData | null>(null);
  const [emailBody, setEmailBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { confirmationId } = useParams<{ confirmationId: string }>();

  useEffect(() => {
    const fetchEmailTemplate = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/registration-confirmations/email/${confirmationId}/email-template`);
        setEmailData(response.data);
        setEmailBody(response.data.templateText);
      } catch (err) {
        //handleApiError(err, 'Fehler beim Laden der E-Mail-Vorlage.');
        setError('Die E-Mail-Vorlage konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    fetchEmailTemplate();
  }, [confirmationId, registrationConfirmationId]);

  const handleSendEmail = async () => {
    try {
      setLoading(true);
      await axios.post(`/api/registration-confirmations/email/${confirmationId}/send-email`, {
        emailBody
      });
      setSuccess(true);
      const confirmation = await axios.get(`/api/registration-confirmations/id/${confirmationId}`);
      setTimeout(() => {
        // Weiterleitung zur Übersicht nach erfolgreichem Versand
        navigate(`/orga/competition/${confirmation.data.competition._id}`);
      }, 2000);
    } catch (err) {
      //handleApiError(err, 'Fehler beim Versenden der E-Mail.v');
      setError('Die E-Mail konnte nicht versendet werden.');
      setLoading(false);
    }
  };

  const handleCancelClick = () => {
    navigate(-1); // Zurück zur vorherigen Seite
  };

  if (loading && !emailData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !emailData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Meldebestätigung versenden
        </Typography>

        {emailData && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold">Empfänger:</Typography>
              <Typography>{emailData.recipientEmail}</Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold">CC:</Typography>
              <Typography>{emailData.ccEmail}</Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold">Betreff:</Typography>
              <Typography>{emailData.subject}</Typography>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                E-Mail-Text:
              </Typography>
              <TextField
                fullWidth
                multiline
                minRows={10}
                maxRows={20}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                variant="outlined"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" mt={2}>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  onClick={handleCancelClick}
                  disabled={loading}
                >
                  Abbrechen
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleSendEmail}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  {loading ? 'Wird gesendet...' : 'E-Mail versenden'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Snackbar 
        open={success} 
        autoHideDuration={2000} 
        onClose={() => setSuccess(false)}
      >
        <Alert severity="success">
          Die E-Mail wurde erfolgreich versendet!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EmailEditorPage;